import { api, setAuthToken } from "../configs/apiConfig";
import deviceTrustService from "./deviceTrustService";

//Get CAPTCHA for login/registration
export const getCaptcha = async () => {
    try {
        const response = await api.get("/Captcha/generate");
        return response.data;
    } catch (error) {
        console.error("Failed to get CAPTCHA:", error.response?.data || error.message);
        throw error;
    }
};

//Validate CAPTCHA input
export const validateCaptcha = async (captchaKey, captchaValue) => {
    try {
        const response = await api.post("/Captcha/validate", { 
            CaptchaKey: captchaKey, 
            CaptchaCode: captchaValue 
        });
        return response.data;
    } catch (error) {
        console.error("Failed to validate CAPTCHA:", error.response?.data || error.message);
        throw error;
    }
};

//Register function: Creates a new user account
export const register = async (registerData) => {
    try {
        const response = await api.post("/auth/register", registerData);
        return response.data;
    } catch (error) {
        console.error("Registration failed:", error.response?.data || error.message);
        throw error;
    }
};

//Login function: Handles both normal login and 2FA users
export const login = async (credentials) => {
    try {
        const response = await api.post("/auth/login", credentials);
        const data = response.data;

        if (data.requiresTwoFactor) {
            return { requiresTwoFactor: true, userId: data.userId };
        } else {
            setAuthToken(data.accessToken);
            localStorage.setItem("authToken", data.accessToken);
            
            return {
                requiresTwoFactor: false,
                userId: data.userId,
                userType: data.userType,
                userData: data.userData
            };
        }
    } catch (error) {
        console.error("Login failed:", error.response?.data || error.message);
        throw error;
    }
};

//Resend 2FA Code
export const resend2FACode = async () => {
    try {
        const response = await api.post("/auth/resend-2fa");
        return response.data;
    } catch (error) {
        console.error("Failed to send 2FA code:", error.response?.data || error.message);
        throw error;
    }
};

//Verify 2FA Code
export const verifyTwoFactor = async (code) => {
    try {
        const response = await api.post("/auth/verify-2fa", { code });
        const data = response.data;

        setAuthToken(data.accessToken);
        localStorage.setItem("authToken", data.accessToken);

        return {
            userId: data.userId,
            userType: data.userType,
            userData: data.userData,
            status: 200
        };
    } catch (error) {
        console.error("2FA Verification failed:", error.response?.data || error.message);
        return { status: 401 };
    }
};

//Change Password Function
export const changePassword = async (userId, currentPassword, newPassword, confirmNewPassword) => {
    try {
        const response = await api.post("/auth/change-password", { 
            CurrentPassword: currentPassword,
            NewPassword: newPassword,
            ConfirmNewPassword: confirmNewPassword
        });
        return response.data;
    } catch (error) {
        // Handle 500 error for incorrect current password
        if (error.response?.status === 500) {
            throw new Error('Incorrect current password');
        }
        // For other errors, use the error message from the response or a default message
        const errorMessage = error.response?.data?.message || 'Failed to change password';
        throw new Error(errorMessage);
    }
};

//Refresh JWT Token
export const refreshToken = async () => {
    try {
        const response = await api.post("/auth/refresh-token");
        const data = response.data;

        if (data.accessToken) {
            setAuthToken(data.accessToken);
            localStorage.setItem("authToken", data.accessToken);
        }
        return data;
    } catch (error) {
        console.error("Failed to refresh token:", error.response?.data || error.message);
        throw error;
    }
};

//Check if user is authenticated
export const getUserInfo = async () => {
    try {
        const token = localStorage.getItem("authToken");
        if (!token) return null;

        setAuthToken(token);
        const response = await api.get("/auth/me");
        return response.data;
    } catch (error) {
        return null;
    }
};

//Logout function
export const logout = () => {
    setAuthToken(null);
    localStorage.removeItem("authToken");
    // Do not clear device trust on logout
    // deviceTrustService.clearTrust();
};

const authService = {
    getCaptcha,
    validateCaptcha,
    register,
    login,
    resend2FACode,
    verifyTwoFactor,
    changePassword,
    refreshToken,
    getUserInfo,
    logout
};

export default authService;
